import type {
    AccountCategory,
    AccountSummary,
    HolisticCategory,
} from '@fruitful/types';
import { Provider } from '@fruitful/types';

import type { useUpsertOrionAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertOrionAccount';
import type { useUpsertQuilttAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertQuilttAccount';
import type { useUpsertSpinwheelCreditCardAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertSpinwheelCreditCardAccount';
import type { useUpsertSpinwheelLoanAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertSpinwheelLoanAccount';
import type { useUpsertSyncteraAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertSyncteraAccount';

type UpsertQuiltt = ReturnType<
    typeof useUpsertQuilttAccountAndRefetch
>['upsertQuilttAccountAsync'];
type UpsertSynctera = ReturnType<
    typeof useUpsertSyncteraAccountAndRefetch
>['upsertSyncteraAccountAsync'];
type UpsertOrion = ReturnType<
    typeof useUpsertOrionAccountAndRefetch
>['upsertOrionAccountAsync'];
type UpsertSpinwheelCc = ReturnType<
    typeof useUpsertSpinwheelCreditCardAccountAndRefetch
>['upsertSpinwheelCreditCardAccountAsync'];
type UpsertSpinwheelLoan = ReturnType<
    typeof useUpsertSpinwheelLoanAccountAndRefetch
>['upsertSpinwheelLoanAccountAsync'];

export interface MoneyMapperLinkedHolisticPersistDeps {
    upsertQuilttAccountAsync: UpsertQuiltt;
    upsertSyncteraAccountAsync: UpsertSynctera;
    upsertOrionAccountAsync: UpsertOrion;
    upsertSpinwheelCreditCardAccountAsync: UpsertSpinwheelCc;
    upsertSpinwheelLoanAccountAsync: UpsertSpinwheelLoan;
}

const holisticUpper = (c: AccountCategory): string => c.toUpperCase();

export const persistHolisticCategoryForAccount = async (
    account: AccountSummary,
    nextHolistic: AccountCategory,
    deps: MoneyMapperLinkedHolisticPersistDeps,
): Promise<void> => {
    const { provider } = account;
    const holisticCategory = holisticUpper(nextHolistic);

    if (provider === Provider.QUILTT) {
        if (account.userId == null) {
            throw new Error('Quiltt account missing userId');
        }
        await deps.upsertQuilttAccountAsync({
            id: account.id,
            userId: account.userId,
            name: account.name,
            description: account.description ?? '',
            interestRate: account.interestRate ?? 0,
            minimumPayment: account.minimumPayment ?? 0,
            holisticCategory,
        });
        return;
    }

    if (provider === Provider.SYNCTERA) {
        await deps.upsertSyncteraAccountAsync({
            id: account.id,
            name: account.name,
            holisticCategory,
        });
        return;
    }

    if (provider === Provider.ORION) {
        if (account.userId == null) {
            throw new Error('Orion account missing userId');
        }
        await deps.upsertOrionAccountAsync({
            id: account.id,
            userId: account.userId,
            name: account.name,
            holisticCategory,
        });
        return;
    }

    if (provider === Provider.SPINWHEEL) {
        const isCredit = account.type?.toLowerCase() === 'credit';
        const body = {
            id: account.id,
            displayName: account.name,
            ...(account.userId != null ? { userId: account.userId } : {}),
            holisticCategory: holisticCategory as HolisticCategory,
            minimumPaymentAmountCents: account.minimumPayment ?? 0,
            interestRate:
                account.interestRate != null && account.interestRate > 0
                    ? account.interestRate
                    : null,
            description: account.description ?? '',
        };
        if (isCredit) {
            await deps.upsertSpinwheelCreditCardAccountAsync(body);
        } else {
            await deps.upsertSpinwheelLoanAccountAsync(body);
        }
        return;
    }

    throw new Error(
        `Holistic update from Current assets is not supported for provider ${provider}`,
    );
};

/** Persists only the display name — same providers as nickname field in AccountEditModal. */
export const persistLinkedAccountNicknameForAccount = async (
    account: AccountSummary,
    nextName: string,
    deps: MoneyMapperLinkedHolisticPersistDeps,
): Promise<void> => {
    const trimmed = nextName.trim();
    if (trimmed === '') {
        throw new Error('Name is required.');
    }

    const holisticCategory = holisticUpper(account.category);
    const { provider } = account;

    if (provider === Provider.QUILTT) {
        if (account.userId == null) {
            throw new Error('Quiltt account missing userId');
        }
        await deps.upsertQuilttAccountAsync({
            id: account.id,
            userId: account.userId,
            name: trimmed,
            description: account.description ?? '',
            interestRate: account.interestRate ?? 0,
            minimumPayment: account.minimumPayment ?? 0,
            holisticCategory,
        });
        return;
    }

    if (provider === Provider.SYNCTERA) {
        await deps.upsertSyncteraAccountAsync({
            id: account.id,
            name: trimmed,
            holisticCategory,
        });
        return;
    }

    if (provider === Provider.ORION) {
        if (account.userId == null) {
            throw new Error('Orion account missing userId');
        }
        await deps.upsertOrionAccountAsync({
            id: account.id,
            userId: account.userId,
            name: trimmed,
            holisticCategory,
        });
        return;
    }

    if (provider === Provider.SPINWHEEL) {
        const isCredit = account.type?.toLowerCase() === 'credit';
        const body = {
            id: account.id,
            displayName: trimmed,
            ...(account.userId != null ? { userId: account.userId } : {}),
            holisticCategory: holisticCategory as HolisticCategory,
            minimumPaymentAmountCents: account.minimumPayment ?? 0,
            interestRate:
                account.interestRate != null && account.interestRate > 0
                    ? account.interestRate
                    : null,
            description: account.description ?? '',
        };
        if (isCredit) {
            await deps.upsertSpinwheelCreditCardAccountAsync(body);
        } else {
            await deps.upsertSpinwheelLoanAccountAsync(body);
        }
        return;
    }

    throw new Error(
        `Nickname update from Current assets is not supported for provider ${provider}`,
    );
};
